import React, { useEffect, useState, useCallback, useRef } from 'react';
import Layout from '../components/Layout';
import { TransacoesService, ContasService, CategoriasService, CartoesService, AnexosService, fmtMoeda, fmtData, mesAtual } from '../services';

const initForm = {
  tipo: 'despesa', descricao: '', valor: '', data: new Date().toISOString().split('T')[0],
  categoria_id: '', conta_id: '', cartao_id: '', status: 'efetivado', observacoes: '',
  dividir: false, conta_dividir_id: '', copiar_conta_id: '', recorrente_meses: 0,
};

export default function Transacoes() {
  const [transacoes, setTransacoes] = useState([]);
  const [contas, setContas] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [cartoes, setCartoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modoFiltro, setModoFiltro] = useState('mes'); // 'mes' | 'periodo'
  const [mes, setMes] = useState(mesAtual());
  const hoje = new Date().toISOString().split('T')[0];
  const primeiroDiaMes = hoje.substring(0, 7) + '-01';
  const [inicio, setInicio] = useState(primeiroDiaMes);
  const [fim, setFim] = useState(hoje);
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroConta, setFiltroConta] = useState('');
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(initForm);
  const [saving, setSaving] = useState(false);
  const [erro, setErro] = useState('');

  // Modal de visualização
  const [viewing, setViewing] = useState(null);
  const [viewAnexos, setViewAnexos] = useState([]);

  // Sorting
  const [sortKey, setSortKey] = useState('data');
  const [sortDir, setSortDir] = useState('desc');

  // Saldo anterior ao período
  const [saldoAnterior, setSaldoAnterior] = useState(null);

  // Estados de anexos
  const [pendingFiles, setPendingFiles] = useState([]);   // criação: arquivos aguardando
  const [anexos, setAnexos] = useState([]);               // edição: anexos já salvos
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [valorDisplay, setValorDisplay] = useState('');
  const fileInputRef = useRef(null);
  const fileInputEditRef = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = { limit: 200 };
    if (modoFiltro === 'periodo') { params.inicio = inicio; params.fim = fim; }
    else params.mes = mes;
    if (filtroTipo) params.tipo = filtroTipo;
    if (filtroConta) params.conta_id = filtroConta;
    TransacoesService.listar(params).then(d => {
      setTransacoes(d.transacoes);
    }).finally(() => setLoading(false));
  }, [modoFiltro, mes, inicio, fim, filtroTipo, filtroConta]);

  useEffect(() => {
    load();
    ContasService.listar().then(setContas);
    CategoriasService.listar().then(setCategorias);
    CartoesService.listar().then(setCartoes);
  }, [load]);

  useEffect(() => {
    let antes_de;
    if (modoFiltro === 'mes') antes_de = mes + '-01';
    else if (modoFiltro === 'periodo' && inicio) antes_de = inicio;
    else { setSaldoAnterior(null); return; }
    const params = { antes_de };
    if (filtroConta) params.conta_id = filtroConta;
    TransacoesService.saldoAnterior(params)
      .then(d => setSaldoAnterior(d.saldo ?? 0))
      .catch(() => setSaldoAnterior(null));
  }, [modoFiltro, mes, inicio, filtroConta]);

  const catFiltradas = categorias.filter(c =>
    !form.tipo || c.tipo === form.tipo || c.tipo === 'ambos'
  );

  const openView = (t) => {
    setViewing(t);
    setViewAnexos([]);
    AnexosService.listar(t.id).then(setViewAnexos).catch(() => setViewAnexos([]));
  };

  const fmtValorDisplay = (valor) => {
    const cents = Math.round(parseFloat(valor || 0) * 100);
    if (!cents) return '';
    return (cents / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const handleValorChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '');
    if (!digits) { setValorDisplay(''); setForm(p => ({ ...p, valor: '' })); return; }
    const cents = parseInt(digits, 10);
    const float = cents / 100;
    setValorDisplay(float.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
    setForm(p => ({ ...p, valor: float }));
  };

  const openNew = () => {
    setEditing(null);
    setForm(initForm);
    setValorDisplay('');
    setPendingFiles([]);
    setAnexos([]);
    setErro('');
    setModal(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({ ...t, data: String(t.data).substring(0, 10), conta_id: t.conta_id || '', cartao_id: t.cartao_id || '', categoria_id: t.categoria_id || '', dividir: false, conta_dividir_id: '', copiar_conta_id: '' });
    setValorDisplay(fmtValorDisplay(t.valor));
    setPendingFiles([]);
    setAnexos([]);
    setErro('');
    setModal(true);
    AnexosService.listar(t.id).then(setAnexos).catch(() => setAnexos([]));
  };

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true); setErro('');
    const payload = { ...form };
    if (!form._copiarAtivo) payload.copiar_conta_id = '';
    delete payload._copiarAtivo;

    if (!payload.conta_id && !payload.cartao_id) {
      setErro('Selecione uma conta ou cartão'); setSaving(false); return;
    }
    if (payload.conta_id) payload.cartao_id = null;
    else payload.conta_id = null;

    if (payload.dividir && !payload.conta_dividir_id) {
      setErro('Selecione a conta do responsável para dividir a despesa'); setSaving(false); return;
    }
    if (payload.copiar_conta_id && String(payload.copiar_conta_id) === String(payload.conta_id)) {
      setErro('A conta de cópia deve ser diferente da conta principal'); setSaving(false); return;
    }

    if (editing) {
      delete payload.dividir;
      delete payload.conta_dividir_id;
      delete payload.copiar_conta_id;
      delete payload.recorrente_meses;
    }

    try {
      let saved;
      if (editing) {
        saved = await TransacoesService.atualizar(editing.id, payload);
      } else {
        saved = await TransacoesService.criar(payload);
      }

      // Upload de arquivos pendentes para a transação principal e eventuais cópias
      if (pendingFiles.length > 0 && saved?.id) {
        setUploadingFiles(true);
        const destinos = [saved.id];
        if (saved.transacao_dividida_id) destinos.push(saved.transacao_dividida_id);
        if (saved.transacao_copia_id) destinos.push(saved.transacao_copia_id);
        for (const tId of destinos) {
          for (const file of pendingFiles) {
            try { await AnexosService.upload(tId, file); } catch (_) {}
          }
        }
        setUploadingFiles(false);
      }

      setModal(false); load();
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir esta transação?')) return;
    try { await TransacoesService.excluir(id); load(); }
    catch (err) { alert(err.response?.data?.erro || 'Erro ao excluir'); }
  };

  const handlePendingFiles = (e) => {
    const files = Array.from(e.target.files);
    setPendingFiles(prev => [...prev, ...files]);
    e.target.value = '';
  };

  const removePendingFile = (idx) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const handleUploadEdit = async (e) => {
    const files = Array.from(e.target.files);
    e.target.value = '';
    if (!editing || !files.length) return;
    setUploadingFiles(true);
    for (const file of files) {
      try {
        const novo = await AnexosService.upload(editing.id, file);
        setAnexos(prev => [...prev, novo]);
      } catch (_) {}
    }
    setUploadingFiles(false);
  };

  const handleDeleteAnexo = async (anexoId) => {
    if (!window.confirm('Excluir este anexo?')) return;
    try {
      await AnexosService.excluir(anexoId);
      setAnexos(prev => prev.filter(a => a.id !== anexoId));
    } catch (_) {}
  };

  const totalReceitas = transacoes.filter(t => t.tipo === 'receita' && t.status === 'efetivado').reduce((s, t) => s + parseFloat(t.valor), 0);
  const totalDespesas = transacoes.filter(t => t.tipo === 'despesa' && t.status === 'efetivado').reduce((s, t) => s + parseFloat(t.valor), 0);
  const pendReceitas  = transacoes.filter(t => t.tipo === 'receita' && t.status !== 'efetivado').reduce((s, t) => s + parseFloat(t.valor), 0);
  const pendDespesas  = transacoes.filter(t => t.tipo === 'despesa' && t.status !== 'efetivado').reduce((s, t) => s + parseFloat(t.valor), 0);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const sortedTransacoes = [...transacoes].sort((a, b) => {
    let va, vb;
    if (sortKey === 'valor') { va = parseFloat(a.valor); vb = parseFloat(b.valor); }
    else if (sortKey === 'conta_nome') { va = (a.conta_nome || a.cartao_nome || '').toLowerCase(); vb = (b.conta_nome || b.cartao_nome || '').toLowerCase(); }
    else if (sortKey === 'categoria_nome') { va = (a.categoria_nome || '').toLowerCase(); vb = (b.categoria_nome || '').toLowerCase(); }
    else { va = (a[sortKey] || '').toString().toLowerCase(); vb = (b[sortKey] || '').toString().toLowerCase(); }
    if (va < vb) return sortDir === 'asc' ? -1 : 1;
    if (va > vb) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const SortIcon = ({ col }) => {
    const active = sortKey === col;
    return (
      <svg width="10" height="10" viewBox="0 0 10 14" fill="none" style={{ marginLeft: 4, opacity: active ? 1 : .35, transition: 'opacity .15s' }}>
        <path d="M5 1L1 5h8L5 1z" fill={active && sortDir === 'asc' ? 'currentColor' : 'var(--gray-400)'} />
        <path d="M5 13L9 9H1l4 4z" fill={active && sortDir === 'desc' ? 'currentColor' : 'var(--gray-400)'} />
      </svg>
    );
  };

  const valDividido = form.valor && form.dividir ? parseFloat(form.valor) / 2 : 0;

  const fmtTamanho = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const fi = {
    padding: '8px 12px', border: '1.5px solid var(--gray-300)', borderRadius: 9,
    fontSize: 13, fontWeight: 500, background: 'white', fontFamily: 'inherit',
    boxShadow: '0 1px 5px rgba(0,0,0,.08)', color: 'var(--gray-700)',
    transition: 'border-color .15s, box-shadow .15s',
  };
  const fiActive = { borderColor: 'var(--primary)', background: 'var(--primary-light)', color: 'var(--primary)', fontWeight: 700 };

  return (
    <Layout title="Transações">
      {/* Toolbar */}
      <div className="flex-between mb-3" style={{ flexWrap: 'wrap', gap: 10 }}>

        {/* ── Filtros ── */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>

          {/* Toggle Mês / Período — estilo segmented control */}
          <div style={{
            display: 'flex', background: 'var(--gray-200)', borderRadius: 10,
            padding: 3, boxShadow: 'inset 0 1px 3px rgba(0,0,0,.1)',
          }}>
            {['mes', 'periodo'].map(modo => (
              <button key={modo} type="button" onClick={() => setModoFiltro(modo)} style={{
                padding: '6px 14px', fontSize: 12.5, fontWeight: 600, border: 'none', cursor: 'pointer',
                borderRadius: 8, letterSpacing: '.01em', transition: 'all .2s ease',
                background: modoFiltro === modo
                  ? 'linear-gradient(135deg, var(--primary), #4f46e5)' : 'transparent',
                color: modoFiltro === modo ? 'white' : 'var(--gray-600)',
                boxShadow: modoFiltro === modo ? '0 2px 6px rgba(37,99,235,.35)' : 'none',
              }}>
                {modo === 'mes' ? 'Mês' : 'Período'}
              </button>
            ))}
          </div>

          {modoFiltro === 'mes' ? (
            <input type="month" value={mes} onChange={e => setMes(e.target.value)} style={fi} />
          ) : (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="date" value={inicio} onChange={e => setInicio(e.target.value)} style={fi} />
              <span style={{ fontSize: 12, color: 'var(--gray-400)', fontWeight: 600 }}>até</span>
              <input type="date" value={fim} onChange={e => setFim(e.target.value)} style={fi} />
            </div>
          )}

          <select value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}
            style={{ ...fi, width: 148, ...(filtroTipo ? fiActive : {}) }}>
            <option value="">Todos os tipos</option>
            <option value="receita">⬆️ Receitas</option>
            <option value="despesa">⬇️ Despesas</option>
          </select>

          <select value={filtroConta} onChange={e => setFiltroConta(e.target.value)}
            style={{ ...fi, width: 168, ...(filtroConta ? fiActive : {}) }}>
            <option value="">Todas as contas</option>
            {contas.map(c => <option key={c.id} value={c.id}>{c.icone} {c.nome}</option>)}
          </select>
        </div>

        {/* ── Ações: botão + totais ── */}
        <div className="flex-gap topbar-actions">
          <button className="btn btn-cta" onClick={openNew}>+ Nova Transação</button>

          {saldoAnterior !== null && (() => {
            const pos = saldoAnterior >= 0;
            return (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', padding: '5px 14px', borderRadius: 10, minWidth: 115, background: 'linear-gradient(135deg,#f8fafc,#f1f5f9)', border: '1px solid #cbd5e1', boxShadow: '0 2px 8px rgba(100,116,139,.10)' }}>
                <span style={{ fontSize: 10, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em' }}>Saldo Anterior</span>
                <span style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.4, color: pos ? 'var(--primary)' : 'var(--danger)' }}>{pos ? '+' : ''}{fmtMoeda(saldoAnterior)}</span>
              </div>
            );
          })()}

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', padding: '5px 14px', background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)', borderRadius: 10, border: '1px solid #86efac', boxShadow: '0 2px 8px rgba(22,163,74,.12)', minWidth: 115 }}>
            <span style={{ fontSize: 10, color: 'var(--success)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em' }}>Receitas</span>
            <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--success)', lineHeight: 1.4 }}>+{fmtMoeda(totalReceitas)}</span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', padding: '5px 14px', background: 'linear-gradient(135deg, #fff5f5, #fee2e2)', borderRadius: 10, border: '1px solid #fca5a5', boxShadow: '0 2px 8px rgba(220,38,38,.12)', minWidth: 115 }}>
            <span style={{ fontSize: 10, color: 'var(--danger)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em' }}>Despesas</span>
            <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--danger)', lineHeight: 1.4 }}>-{fmtMoeda(totalDespesas)}</span>
          </div>

          {(() => {
            const saldoFinal = (saldoAnterior ?? 0) + totalReceitas - totalDespesas;
            const positivo = saldoFinal >= 0;
            const label = saldoAnterior !== null ? 'Saldo Final' : 'Total';
            return (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', padding: '5px 14px', borderRadius: 10, minWidth: 115, border: `1px solid ${positivo ? '#93c5fd' : '#fca5a5'}`, boxShadow: `0 2px 8px ${positivo ? 'rgba(37,99,235,.12)' : 'rgba(220,38,38,.12)'}`, background: positivo ? 'linear-gradient(135deg,#eff6ff,#dbeafe)' : 'linear-gradient(135deg,#fff5f5,#fee2e2)' }}>
                <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', color: positivo ? 'var(--primary)' : 'var(--danger)' }}>{label}</span>
                <span style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.4, color: positivo ? 'var(--primary)' : 'var(--danger)' }}>{positivo ? '+' : ''}{fmtMoeda(saldoFinal)}</span>
              </div>
            );
          })()}

          {(pendReceitas > 0 || pendDespesas > 0) && (() => {
            const liquido = pendReceitas - pendDespesas;
            const positivo = liquido >= 0;
            return (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', padding: '5px 14px', borderRadius: 10, minWidth: 115, border: '1px solid #fde68a', boxShadow: '0 2px 8px rgba(217,119,6,.15)', background: 'linear-gradient(135deg,#fffbeb,#fef3c7)' }}>
                <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', color: '#92400e' }}>⏳ A Efetivar</span>
                <span style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.4, color: positivo ? '#b45309' : '#b91c1c' }}>{positivo ? '+' : ''}{fmtMoeda(liquido)}</span>
              </div>
            );
          })()}

          {(pendReceitas > 0 || pendDespesas > 0) && (() => {
            const saldoFinal = (saldoAnterior ?? 0) + totalReceitas - totalDespesas;
            const projecao = saldoFinal + pendReceitas - pendDespesas;
            const positivo = projecao >= 0;
            return (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', padding: '5px 14px', borderRadius: 10, minWidth: 130, border: '1px solid #fde68a', boxShadow: '0 2px 8px rgba(217,119,6,.15)', background: 'linear-gradient(135deg,#fffbeb,#fef3c7)' }}>
                <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', color: '#92400e' }}>⏳ Projeção Final</span>
                <span style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.4, color: positivo ? '#b45309' : '#b91c1c' }}>{positivo ? '+' : ''}{fmtMoeda(projecao)}</span>
              </div>
            );
          })()}
        </div>
      </div>

      <div className="card">
        {loading ? (
          <div className="loading-wrap"><div className="spinner" /></div>
        ) : transacoes.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">💳</div>
            <p>Nenhuma transação encontrada.</p>
          </div>
        ) : (
          <>
            {/* Tabela — desktop */}
            <div className="table-wrap extrato-desktop">
              <table>
                <thead>
                  <tr>
                    {[
                      { label: 'Descrição',    col: 'descricao' },
                      { label: 'Categoria',    col: 'categoria_nome' },
                      { label: 'Data',         col: 'data' },
                      { label: 'Conta/Cartão', col: 'conta_nome' },
                      { label: 'Status',       col: 'status' },
                      { label: 'Valor',        col: 'valor' },
                    ].map(({ label, col }) => (
                      <th key={col} onClick={() => handleSort(col)} style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', color: sortKey === col ? 'var(--primary)' : undefined }}>
                          {label}<SortIcon col={col} />
                        </span>
                      </th>
                    ))}
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {sortedTransacoes.map(t => (
                    <tr key={t.id}>
                      <td>
                        <div style={{ fontWeight: 500, fontSize: 13 }}>{t.descricao}</div>
                        {t.observacoes && <div className="text-sm text-muted">{t.observacoes}</div>}
                      </td>
                      <td>
                        <span style={{ fontSize: 13 }}>
                          {t.categoria_icone} {t.categoria_nome || '—'}
                        </span>
                      </td>
                      <td style={{ fontSize: 13 }}>{fmtData(t.data)}</td>
                      <td style={{ fontSize: 13 }}>{t.conta_nome || t.cartao_nome || '—'}</td>
                      <td>
                        <span className={`badge ${t.status === 'efetivado' ? 'badge-success' : t.status === 'pendente' ? 'badge-warning' : 'badge-gray'}`}>
                          {t.status}
                        </span>
                      </td>
                      <td className={t.tipo === 'receita' ? 'value-positive' : 'value-negative'} style={{ fontWeight: 600 }}>
                        {t.tipo === 'receita' ? '+' : '-'}{fmtMoeda(t.valor)}
                      </td>
                      <td>
                        <div className="flex-gap gap-sm">
                          <button className="btn-action btn-action-view" title="Visualizar" onClick={() => openView(t)}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M2 12s3.636-7 10-7 10 7 10 7-3.636 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/>
                            </svg>
                          </button>
                          <button className="btn-action btn-action-edit" title="Editar" onClick={() => openEdit(t)}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/>
                            </svg>
                          </button>
                          <button className="btn-action btn-action-delete" title="Excluir" onClick={() => handleDelete(t.id)}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/>
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Cards — mobile */}
            <div className="extrato-mobile">
              {sortedTransacoes.map(t => (
                <div key={t.id} className="extrato-item">
                  <div className="extrato-item-top">
                    <div style={{ flex: 1, minWidth: 0, marginRight: 8 }}>
                      <div className="extrato-item-desc">{t.descricao}</div>
                      <div className="extrato-item-meta">
                        {t.categoria_icone && <span>{t.categoria_icone}</span>}
                        <span>{t.categoria_nome || '—'}</span>
                        <span style={{ color: 'var(--gray-300)' }}>·</span>
                        <span>{fmtData(t.data)}</span>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div className={`extrato-item-valor ${t.tipo === 'receita' ? 'value-positive' : 'value-negative'}`}>
                        {t.tipo === 'receita' ? '+' : '-'}{fmtMoeda(t.valor)}
                      </div>
                      <span className={`badge ${t.status === 'efetivado' ? 'badge-success' : t.status === 'pendente' ? 'badge-warning' : 'badge-gray'}`} style={{ fontSize: 10 }}>
                        {t.status}
                      </span>
                    </div>
                  </div>
                  <div className="extrato-item-bottom">
                    <span style={{ fontSize: 12, color: 'var(--gray-400)' }}>
                      {t.conta_nome || t.cartao_nome || '—'}
                    </span>
                    <div className="flex-gap gap-sm">
                      <button className="btn-action btn-action-view" title="Visualizar" onClick={() => openView(t)}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M2 12s3.636-7 10-7 10 7 10 7-3.636 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/>
                        </svg>
                      </button>
                      <button className="btn-action btn-action-edit" title="Editar" onClick={() => openEdit(t)}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/>
                        </svg>
                      </button>
                      <button className="btn-action btn-action-delete" title="Excluir" onClick={() => handleDelete(t.id)}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal modal-md" style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

            {/* Header dinâmico — muda cor com o tipo */}
            <div style={{
              padding: '16px 22px',
              background: form.tipo === 'receita'
                ? 'linear-gradient(135deg,#f0fdf4,#dcfce7)'
                : 'linear-gradient(135deg,#fff5f5,#fee2e2)',
              borderBottom: `2px solid ${form.tipo === 'receita' ? '#86efac' : '#fca5a5'}`,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.09em', color: 'var(--gray-400)', marginBottom: 2 }}>
                  {editing ? 'Editar' : 'Nova'} Transação
                </div>
                <div style={{ fontSize: 16, fontWeight: 700, color: form.tipo === 'receita' ? 'var(--success)' : 'var(--danger)' }}>
                  {form.tipo === 'receita' ? '⬆️ Receita' : '⬇️ Despesa'}
                </div>
              </div>
              <button className="modal-close" onClick={() => setModal(false)}>×</button>
            </div>

            <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
              <div className="modal-body" style={{ background: '#f7f9fc', gap: 14, overflowY: 'auto', flex: 1 }}>
                {erro && <div className="alert alert-danger">{erro}</div>}

                {/* Seletor de tipo — botões grandes */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  {[
                    { value: 'receita', label: 'Receita', color: 'var(--success)', shadow: 'rgba(22,163,74,.22)', bg: 'linear-gradient(135deg,#f0fdf4,#dcfce7)', border: '#86efac', sinal: '↑' },
                    { value: 'despesa', label: 'Despesa', color: 'var(--danger)',  shadow: 'rgba(220,38,38,.22)', bg: 'linear-gradient(135deg,#fff5f5,#fee2e2)', border: '#fca5a5', sinal: '↓' },
                  ].map(({ value, label, color, shadow, bg, border, sinal }) => (
                    <button key={value} type="button"
                      onClick={() => setForm(p => ({ ...p, tipo: value, categoria_id: '', dividir: false, conta_dividir_id: '', copiar_conta_id: '' }))}
                      style={{
                        padding: '13px 10px', border: `2px solid ${form.tipo === value ? border : 'var(--gray-200)'}`,
                        borderRadius: 12, cursor: 'pointer', fontWeight: 700, fontSize: 14,
                        background: form.tipo === value ? bg : 'white',
                        color: form.tipo === value ? color : 'var(--gray-400)',
                        boxShadow: form.tipo === value ? `0 4px 14px ${shadow}` : '0 1px 3px rgba(0,0,0,.06)',
                        transition: 'all .2s ease',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9,
                      }}>
                      <span style={{
                        width: 28, height: 28, borderRadius: '50%',
                        background: form.tipo === value ? color : 'var(--gray-300)',
                        color: 'white', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 15, fontWeight: 900, flexShrink: 0, transition: 'all .2s',
                        boxShadow: form.tipo === value ? `0 2px 6px ${shadow}` : 'none',
                      }}>{sinal}</span>
                      {label}
                    </button>
                  ))}
                </div>

                {/* Descrição */}
                <div className="form-group">
                  <label className="form-label">Descrição *</label>
                  <input className="form-control" required value={form.descricao}
                    placeholder="Ex: Salário, Aluguel, Mercado..."
                    onChange={e => setForm(p => ({ ...p, descricao: e.target.value }))} />
                </div>

                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Valor (R$) *</label>
                    <input type="text" inputMode="numeric" className="form-control" required
                      placeholder="0,00" value={valorDisplay}
                      onChange={handleValorChange} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Data *</label>
                    <input type="date" className="form-control" required value={form.data}
                      onChange={e => setForm(p => ({ ...p, data: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Categoria</label>
                    <select className="form-control" value={form.categoria_id}
                      onChange={e => setForm(p => ({ ...p, categoria_id: e.target.value }))}>
                      <option value="">Sem categoria</option>
                      {catFiltradas.map(c => <option key={c.id} value={c.id}>{c.icone} {c.nome}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Status</label>
                    <select className="form-control" value={form.status}
                      onChange={e => setForm(p => ({ ...p, status: e.target.value }))}>
                      <option value="efetivado">Efetivado</option>
                      <option value="pendente">Pendente</option>
                    </select>
                  </div>

                  {form.tipo === 'despesa' ? (
                    <>
                      <div className="form-group">
                        <label className="form-label">Conta bancária</label>
                        <select className="form-control" value={form.conta_id}
                          onChange={e => setForm(p => ({ ...p, conta_id: e.target.value, cartao_id: '', conta_dividir_id: '', copiar_conta_id: '' }))}>
                          <option value="">— selecionar —</option>
                          {contas.map(c => <option key={c.id} value={c.id}>{c.icone} {c.nome}</option>)}
                        </select>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Cartão de crédito</label>
                        <select className="form-control" value={form.cartao_id}
                          onChange={e => setForm(p => ({ ...p, cartao_id: e.target.value, conta_id: '', copiar_conta_id: '' }))}>
                          <option value="">— selecionar —</option>
                          {cartoes.map(c => <option key={c.id} value={c.id}>💳 {c.nome}</option>)}
                        </select>
                      </div>
                    </>
                  ) : (
                    <div className="form-group" style={{ gridColumn: '1/-1' }}>
                      <label className="form-label">Conta bancária *</label>
                      <select className="form-control" value={form.conta_id}
                        onChange={e => setForm(p => ({ ...p, conta_id: e.target.value, copiar_conta_id: '' }))}>
                        <option value="">— selecionar —</option>
                        {contas.map(c => <option key={c.id} value={c.id}>{c.icone} {c.nome}</option>)}
                      </select>
                    </div>
                  )}

                  {/* Dividir — toggle switch */}
                  {form.tipo === 'despesa' && !editing && (
                    <div style={{ gridColumn: '1/-1', display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <div
                        onClick={() => setForm(p => ({ ...p, dividir: !p.dividir, conta_dividir_id: '', copiar_conta_id: !p.dividir ? '' : p.copiar_conta_id, recorrente_meses: !p.dividir ? 0 : p.recorrente_meses }))}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '12px 16px', borderRadius: 12, cursor: 'pointer',
                          background: form.dividir ? '#fff7ed' : 'white',
                          border: `1.5px solid ${form.dividir ? '#fed7aa' : 'var(--gray-200)'}`,
                          boxShadow: form.dividir ? '0 2px 10px rgba(217,119,6,.14)' : '0 1px 3px rgba(0,0,0,.06)',
                          transition: 'all .2s', userSelect: 'none',
                        }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <div style={{ width: 36, height: 36, borderRadius: 9, background: form.dividir ? '#fff7ed' : 'var(--gray-100)', border: `1px solid ${form.dividir ? '#fed7aa' : 'var(--gray-200)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, flexShrink: 0 }}>✂️</div>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: form.dividir ? '#92400e' : 'var(--gray-700)' }}>Dividir despesa (50%)</div>
                            <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>Lança metade em outra conta</div>
                          </div>
                        </div>
                        <div style={{ width: 40, height: 22, borderRadius: 11, background: form.dividir ? '#d97706' : 'var(--gray-300)', position: 'relative', transition: 'background .25s', flexShrink: 0 }}>
                          <div style={{ width: 18, height: 18, borderRadius: '50%', background: 'white', position: 'absolute', top: 2, left: form.dividir ? 20 : 2, transition: 'left .25s', boxShadow: '0 1px 4px rgba(0,0,0,.25)' }} />
                        </div>
                      </div>
                      {form.dividir && (
                        <div style={{ padding: '14px 16px', background: '#fffbeb', borderRadius: 10, border: '1px solid #fed7aa' }}>
                          <label className="form-label" style={{ marginBottom: 6, color: '#92400e' }}>Conta do responsável *</label>
                          <select className="form-control" value={form.conta_dividir_id}
                            onChange={e => setForm(p => ({ ...p, conta_dividir_id: e.target.value }))}>
                            <option value="">— selecionar conta —</option>
                            {contas.filter(c => String(c.id) !== String(form.conta_id)).map(c => <option key={c.id} value={c.id}>{c.icone} {c.nome}</option>)}
                          </select>
                          {form.valor && (
                            <div style={{ marginTop: 8, fontSize: 12, color: '#92400e', fontWeight: 600 }}>
                              Será lançado <strong>{fmtMoeda(parseFloat(form.valor || 0) / 2)}</strong> como despesa na conta selecionada
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Copiar — toggle switch */}
                  {!editing && (
                    <div style={{ gridColumn: '1/-1', display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <div
                        onClick={() => setForm(p => ({ ...p, _copiarAtivo: !p._copiarAtivo, copiar_conta_id: '', dividir: !p._copiarAtivo ? false : p.dividir, conta_dividir_id: !p._copiarAtivo ? '' : p.conta_dividir_id }))}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '12px 16px', borderRadius: 12, cursor: 'pointer',
                          background: form._copiarAtivo ? '#eff6ff' : 'white',
                          border: `1.5px solid ${form._copiarAtivo ? '#93c5fd' : 'var(--gray-200)'}`,
                          boxShadow: form._copiarAtivo ? '0 2px 10px rgba(37,99,235,.14)' : '0 1px 3px rgba(0,0,0,.06)',
                          transition: 'all .2s', userSelect: 'none',
                        }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <div style={{ width: 36, height: 36, borderRadius: 9, background: form._copiarAtivo ? '#eff6ff' : 'var(--gray-100)', border: `1px solid ${form._copiarAtivo ? '#93c5fd' : 'var(--gray-200)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, flexShrink: 0 }}>📋</div>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: form._copiarAtivo ? 'var(--primary)' : 'var(--gray-700)' }}>Copiar para outra conta</div>
                            <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>Valor integral em outra conta</div>
                          </div>
                        </div>
                        <div style={{ width: 40, height: 22, borderRadius: 11, background: form._copiarAtivo ? 'var(--primary)' : 'var(--gray-300)', position: 'relative', transition: 'background .25s', flexShrink: 0 }}>
                          <div style={{ width: 18, height: 18, borderRadius: '50%', background: 'white', position: 'absolute', top: 2, left: form._copiarAtivo ? 20 : 2, transition: 'left .25s', boxShadow: '0 1px 4px rgba(0,0,0,.25)' }} />
                        </div>
                      </div>
                      {form._copiarAtivo && (
                        <div style={{ padding: '14px 16px', background: '#eff6ff', borderRadius: 10, border: '1px solid #93c5fd' }}>
                          <label className="form-label" style={{ marginBottom: 6, color: 'var(--primary)' }}>Conta destino *</label>
                          <select className="form-control" value={form.copiar_conta_id}
                            onChange={e => setForm(p => ({ ...p, copiar_conta_id: e.target.value }))}>
                            <option value="">— selecionar conta —</option>
                            {contas.filter(c => String(c.id) !== String(form.conta_id)).map(c => <option key={c.id} value={c.id}>{c.icone} {c.nome}</option>)}
                          </select>
                          {form.valor && form.copiar_conta_id && (
                            <div style={{ marginTop: 8, fontSize: 12, color: 'var(--primary)', fontWeight: 600 }}>
                              Será lançado <strong>{fmtMoeda(parseFloat(form.valor || 0))}</strong> como {form.tipo} na conta selecionada
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Repetir mensalmente */}
                  {!editing && !form.dividir && form.conta_id && (
                    <div style={{ gridColumn: '1/-1', display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <div
                        onClick={() => setForm(p => ({ ...p, recorrente_meses: p.recorrente_meses > 0 ? 0 : 3 }))}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '12px 16px', borderRadius: 12, cursor: 'pointer',
                          background: form.recorrente_meses > 0 ? '#f5f3ff' : 'white',
                          border: `1.5px solid ${form.recorrente_meses > 0 ? '#a78bfa' : 'var(--gray-200)'}`,
                          boxShadow: form.recorrente_meses > 0 ? '0 2px 10px rgba(139,92,246,.14)' : '0 1px 3px rgba(0,0,0,.06)',
                          transition: 'all .2s', userSelect: 'none',
                        }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <div style={{ width: 36, height: 36, borderRadius: 9, background: form.recorrente_meses > 0 ? '#f5f3ff' : 'var(--gray-100)', border: `1px solid ${form.recorrente_meses > 0 ? '#a78bfa' : 'var(--gray-200)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, flexShrink: 0 }}>🔁</div>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: form.recorrente_meses > 0 ? '#6d28d9' : 'var(--gray-700)' }}>Repetir mensalmente</div>
                            <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>Gera parcelas pendentes nos próximos meses</div>
                          </div>
                        </div>
                        <div style={{ width: 40, height: 22, borderRadius: 11, background: form.recorrente_meses > 0 ? '#7c3aed' : 'var(--gray-300)', position: 'relative', transition: 'background .25s', flexShrink: 0 }}>
                          <div style={{ width: 18, height: 18, borderRadius: '50%', background: 'white', position: 'absolute', top: 2, left: form.recorrente_meses > 0 ? 20 : 2, transition: 'left .25s', boxShadow: '0 1px 4px rgba(0,0,0,.25)' }} />
                        </div>
                      </div>
                      {form.recorrente_meses > 0 && (
                        <div style={{ padding: '14px 16px', background: '#f5f3ff', borderRadius: 10, border: '1px solid #a78bfa' }}>
                          <label className="form-label" style={{ marginBottom: 8, color: '#6d28d9' }}>Repetir por quantos meses?</label>
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                            {[3, 6, 12, 24, 120].map(n => (
                              <button key={n} type="button"
                                onClick={() => setForm(p => ({ ...p, recorrente_meses: n }))}
                                style={{
                                  padding: '7px 20px', borderRadius: 8,
                                  border: `2px solid ${form.recorrente_meses === n ? '#7c3aed' : '#d8b4fe'}`,
                                  background: form.recorrente_meses === n ? '#7c3aed' : 'white',
                                  color: form.recorrente_meses === n ? 'white' : '#6d28d9',
                                  fontWeight: 700, fontSize: 13, cursor: 'pointer', transition: 'all .15s',
                                }}>
                                {n}x
                              </button>
                            ))}
                          </div>
                          {form.data && (() => {
                            const [y, m, d] = form.data.split('-').map(Number);
                            const totalM = (y * 12 + m - 1) + form.recorrente_meses - 1;
                            const ny = Math.floor(totalM / 12);
                            const nm = (totalM % 12) + 1;
                            const nomes = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
                            return (
                              <div style={{ fontSize: 12, color: '#7c3aed', fontWeight: 500 }}>
                                Esta + {form.recorrente_meses - 1} parcelas pendentes até {nomes[nm - 1]}/{ny}
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="form-group" style={{ gridColumn: '1/-1' }}>
                    <label className="form-label">Observações</label>
                    <input className="form-control" value={form.observacoes || ''} placeholder="Opcional"
                      onChange={e => setForm(p => ({ ...p, observacoes: e.target.value }))} />
                  </div>

                  {/* Seção de Anexos */}
                  <div style={{ gridColumn: '1/-1' }}>
                    <label className="form-label" style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
                      Anexos
                    </label>

                    {/* Lista de anexos salvos (edição) */}
                    {editing && (
                      <div style={{ marginBottom: 8 }}>
                        {anexos.length === 0 && !uploadingFiles && (
                          <p style={{ fontSize: 12, color: 'var(--gray-400)', marginBottom: 8 }}>Nenhum anexo ainda.</p>
                        )}
                        {anexos.map(a => (
                          <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'white', borderRadius: 8, marginBottom: 6, border: '1px solid var(--gray-200)', boxShadow: '0 1px 3px rgba(0,0,0,.05)' }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--gray-400)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                            <span style={{ flex: 1, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--gray-700)', fontWeight: 500 }}>{a.nome_original}</span>
                            {a.tamanho && <span style={{ fontSize: 11, color: 'var(--gray-400)', flexShrink: 0 }}>{fmtTamanho(a.tamanho)}</span>}
                            <a href={AnexosService.urlDownload(a.id)} target="_blank" rel="noreferrer" className="btn-action btn-action-view" style={{ width: 28, height: 28 }} title="Baixar">
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 13, height: 13 }}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                            </a>
                            <button type="button" className="btn-action btn-action-delete" style={{ width: 28, height: 28 }} title="Excluir" onClick={() => handleDeleteAnexo(a.id)}>
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 13, height: 13 }}><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                            </button>
                          </div>
                        ))}
                        {uploadingFiles && <p style={{ fontSize: 12, color: 'var(--primary)', fontWeight: 500 }}>Enviando...</p>}
                      </div>
                    )}

                    {/* Arquivos pendentes (criação) */}
                    {!editing && pendingFiles.length > 0 && (
                      <div style={{ marginBottom: 8 }}>
                        {pendingFiles.map((f, i) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'white', borderRadius: 8, marginBottom: 6, border: '1px solid var(--gray-200)', boxShadow: '0 1px 3px rgba(0,0,0,.05)' }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--gray-400)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                            <span style={{ flex: 1, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--gray-700)', fontWeight: 500 }}>{f.name}</span>
                            <span style={{ fontSize: 11, color: 'var(--gray-400)', flexShrink: 0 }}>{fmtTamanho(f.size)}</span>
                            <button type="button" className="btn-action btn-action-delete" style={{ width: 28, height: 28 }} onClick={() => removePendingFile(i)}>
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 13, height: 13 }}><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Zona de upload */}
                    <input type="file" multiple ref={editing ? fileInputEditRef : fileInputRef} style={{ display: 'none' }}
                      onChange={editing ? handleUploadEdit : handlePendingFiles} />
                    <div
                      onClick={() => !uploadingFiles && (editing ? fileInputEditRef : fileInputRef).current?.click()}
                      style={{
                        border: '2px dashed var(--gray-300)', borderRadius: 10, padding: '14px',
                        textAlign: 'center', cursor: uploadingFiles ? 'not-allowed' : 'pointer',
                        background: 'white', transition: 'all .2s',
                        opacity: uploadingFiles ? 0.6 : 1,
                      }}>
                      <div style={{ fontSize: 20, marginBottom: 4 }}>📎</div>
                      <div style={{ fontSize: 12, color: 'var(--gray-500)', fontWeight: 500 }}>
                        {uploadingFiles ? 'Enviando arquivos...' : 'Clique para adicionar arquivo'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer" style={{ background: '#f7f9fc', borderTop: '1px solid var(--gray-200)' }}>
                {form.dividir && valDividido > 0 && (
                  <span style={{ fontSize: 12, color: 'var(--gray-500)', marginRight: 'auto' }}>
                    Total: {fmtMoeda(parseFloat(form.valor))} · Parte dele: {fmtMoeda(valDividido)}
                  </span>
                )}
                <button type="button" className="btn btn-ghost" onClick={() => setModal(false)}>Cancelar</button>
                <button type="submit" disabled={saving || uploadingFiles} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '9px 22px', borderRadius: 9, border: 'none', cursor: 'pointer',
                  fontWeight: 700, fontSize: 14, color: 'white', transition: 'all .2s',
                  background: form.tipo === 'receita'
                    ? 'linear-gradient(135deg,var(--success),#15803d)'
                    : 'linear-gradient(135deg,var(--danger),#b91c1c)',
                  boxShadow: form.tipo === 'receita'
                    ? '0 4px 14px rgba(22,163,74,.35)'
                    : '0 4px 14px rgba(220,38,38,.35)',
                  opacity: saving || uploadingFiles ? 0.7 : 1,
                }}>
                  {saving ? 'Salvando...' : uploadingFiles ? 'Enviando...' : '✓ Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Modal de visualização */}
      {viewing && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setViewing(null)}>
          <div className="modal modal-md" style={{ overflow: 'hidden' }}>

            {/* Hero full-bleed com gradiente */}
            <div style={{
              padding: '16px 20px 24px',
              background: viewing.tipo === 'receita'
                ? 'linear-gradient(135deg,#16a34a 0%,#22c55e 100%)'
                : 'linear-gradient(135deg,#dc2626 0%,#f87171 100%)',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,.75)', fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 6 }}>
                    {viewing.tipo === 'receita' ? 'Receita' : 'Despesa'}
                  </div>
                  <div style={{ fontSize: 32, fontWeight: 800, color: 'white', lineHeight: 1.1, letterSpacing: '-.02em' }}>
                    {viewing.tipo === 'receita' ? '+' : '-'}{fmtMoeda(viewing.valor)}
                  </div>
                  <div style={{ fontSize: 14, color: 'rgba(255,255,255,.9)', marginTop: 8, fontWeight: 500 }}>
                    {viewing.descricao}
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10, flexShrink: 0 }}>
                  <button onClick={() => setViewing(null)} style={{
                    background: 'rgba(255,255,255,.2)', border: 'none', borderRadius: '50%',
                    width: 30, height: 30, cursor: 'pointer', color: 'white',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
                  }}>×</button>
                  <span style={{
                    background: 'rgba(255,255,255,.25)', backdropFilter: 'blur(4px)',
                    color: 'white', padding: '5px 14px', borderRadius: 100,
                    fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap',
                  }}>
                    {viewing.status === 'efetivado' ? '✓ Efetivado' : '⏳ Pendente'}
                  </span>
                </div>
              </div>
            </div>

            {/* Corpo em fundo claro */}
            <div style={{ background: '#f7f9fc', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>

              {/* Grid de campos como mini-cards */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {[
                  { label: 'Data', value: fmtData(viewing.data), full: false },
                  { label: 'Categoria', value: `${viewing.categoria_icone || ''} ${viewing.categoria_nome || '—'}`.trim(), full: false },
                  { label: viewing.cartao_id ? 'Cartão' : 'Conta', value: viewing.conta_nome || viewing.cartao_nome || '—', full: false },
                  viewing.observacoes ? { label: 'Observações', value: viewing.observacoes, full: true } : null,
                ].filter(Boolean).map(({ label, value, full }) => (
                  <div key={label} style={{
                    background: 'white', borderRadius: 10, padding: '10px 14px',
                    border: '1px solid var(--gray-100)', boxShadow: '0 1px 4px rgba(0,0,0,.05)',
                    gridColumn: full ? '1/-1' : undefined,
                  }}>
                    <div style={{ fontSize: 10, color: 'var(--gray-400)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 4 }}>{label}</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--gray-800)' }}>{value}</div>
                  </div>
                ))}
              </div>

              {/* Anexos */}
              <div>
                <div style={{ fontSize: 10, color: 'var(--gray-400)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 5 }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
                  Anexos
                </div>
                {viewAnexos.length === 0 ? (
                  <p style={{ fontSize: 12, color: 'var(--gray-400)' }}>Nenhum anexo.</p>
                ) : viewAnexos.map(a => (
                  <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', background: 'white', borderRadius: 8, marginBottom: 6, border: '1px solid var(--gray-200)', boxShadow: '0 1px 3px rgba(0,0,0,.05)' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--gray-400)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                    <span style={{ flex: 1, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--gray-700)', fontWeight: 500 }}>{a.nome_original}</span>
                    {a.tamanho && <span style={{ fontSize: 11, color: 'var(--gray-400)', flexShrink: 0 }}>{fmtTamanho(a.tamanho)}</span>}
                    <a href={AnexosService.urlDownload(a.id)} target="_blank" rel="noreferrer" className="btn-action btn-action-view" style={{ width: 28, height: 28 }} title="Baixar">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 13, height: 13 }}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    </a>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="modal-footer" style={{ background: '#f7f9fc', borderTop: '1px solid var(--gray-200)' }}>
              <button type="button" className="btn btn-ghost" onClick={() => setViewing(null)}>Fechar</button>
              <button type="button"
                onClick={() => { setViewing(null); openEdit(viewing); }}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '9px 22px', borderRadius: 9, border: 'none', cursor: 'pointer',
                  fontWeight: 700, fontSize: 14, color: 'white', transition: 'all .2s',
                  background: viewing.tipo === 'receita'
                    ? 'linear-gradient(135deg,var(--success),#15803d)'
                    : 'linear-gradient(135deg,var(--danger),#b91c1c)',
                  boxShadow: viewing.tipo === 'receita'
                    ? '0 4px 14px rgba(22,163,74,.35)'
                    : '0 4px 14px rgba(220,38,38,.35)',
                }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg>
                Editar
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
